import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { useAppToast } from '@/hooks/useAppToast';
import { apiCall } from '@/util/api';
import { useAppConfig, MAX_GRP_TXT_RECIPIENTS } from '@/modules/config';
import { topicGardenEnabled } from '@/conditional_feature/topic_garden_enabled';
import { Contact, ContactList } from '@/modules/contact';
import { useContactState } from '@/modules/contact/ContactStateContext';
import { selectRecipients } from '@/modules/contact/reducer';
import { useUserContext } from '@/modules/user/useUserContext';
import { useDeviceInfo } from '@/hooks/useDeviceInfo';
import { useFCMToken } from '@/hooks/useFCMToken';
import {
  type ContextVars,
  type MessageIntent,
  type SharingMode,
  type TemplateSet,
  type Template,
} from '@/modules/template';
import type { MessageDraft } from '@/modules/message';
import { TopicLogic } from '@/modules/topic/logic';
import type { Topic } from '@/modules/topic/types';
import { effectiveIntroLimit } from '@/modules/topic/intro_limit';
import { useCasbin, ACTIONS, RESOURCES } from '@/contexts/CasbinContext';
import {
  MePrFoService,
  type TopicAwarenessMap,
  type ReshareInfoLike,
} from '@/services/meprfo';
import { ShareWizardLogic } from '@/modules/share/logic';
import { Destinations } from '@/modules/share/navigation_logic';
import { useShareNavigator } from '@/modules/share/useShareNavigator';
import type {
  AddressingMode,
  PreviewSegment,
  ShareFlowShape,
  ShareNavigator,
  ShareSurface,
} from '@/modules/share/types';
import type {
  AnsweredSlot,
  Environment,
  FormFacts,
  SharingFormSlot,
} from '@/modules/form';
import { useWizardSession } from '@/modules/share/useWizardSession';
import { useTopicAwareness } from '@/modules/share/useTopicAwareness';
import { useUpdateRecipientMatch } from '@/modules/share/useUpdateRecipientMatch';
import { useShareFormChoices } from '@/modules/share/useShareFormChoices';
import { useShareRecipients } from '@/modules/share/useShareRecipients';
import { useShareComposer } from '@/modules/share/useShareComposer';
import {
  sharingLegDetailsFor,
  useShareController,
  type LegStoryInfo,
} from '@/modules/share/useShareController';
import {
  OUTBOX_LEG_ID_PLACEHOLDER,
  OutboxLogic,
  useOutbox,
} from '@/modules/outbox';

const ALL_INTERSTITIAL_KEYS: readonly string[] = [
  'contacts',
  'notifications',
  'phoneLinking',
];

interface ShareWizardContextValue {
  topic: Topic | null;
  isTopicLoading: boolean;
  topicError: string | null;
  topicId: string;
  // Update flow only (#427): the head of the shared change list (ISO) and the
  // sender's editable summary of what changed.
  aboutChangesSince: string | undefined;
  /** Whether this session is sharing a change list — `aboutChangesSince` read as a flag. */
  isUpdateFlow: boolean;
  updateSummary: string;
  setUpdateSummary: (value: string) => void;
  /**
   * Which of the intro's two messages this session composes (#447): a `fresh`
   * share is the sender's own first word about the topic, a `reshare` passes on
   * the leg they received. It is read off `previousLegId` — a session holding
   * one is passing that leg on — and it picks the sharing form's slots, the
   * leg type sent, and what the compose step calls itself.
   */
  sharingMode: SharingMode;
  /**
   * Reshare mode, garden dialect only: the sender's first-person connection to
   * whoever sent them the leg they pass on ("I work with"). One answer for the
   * current sharing session, asked once at the top of the composer form by
   * `ConnectionField`. A classic topic never asks it.
   */
  connectionToPrevLeg: string;
  setConnectionToPrevLeg: (value: string) => void;
  // Reshare mode only (#447): the previous leg's info payload
  // (reshare story/race source), null until fetched.
  reshareInfoForPrevLeg: ReshareInfoLike | null;
  // The last initiation response's details payload — the last-confirm step's
  // goal-crossing celebration source (#447).
  lastReshareInfo: ReshareInfoLike | null;

  selectedToneId: string | null;
  setSelectedToneId: (id: string | null) => void;
  singletonMode: boolean;
  setSingletonMode: (value: boolean) => void;
  // Effective singleton mode used for sending and template options: true
  // whenever each message reaches exactly one recipient (raw flag on, or the
  // group-texts toggle hidden because no intent has more than one recipient).
  effectiveSingletonMode: boolean;
  /** Per-intent reading of `effectiveSingletonMode` — see its definition. */
  intentReachesOnePerson: (intent: MessageIntent) => boolean;
  // Whether the "Share as" segment has a choice to offer: false when no intent
  // has more than one recipient, so group mode equals singleton.
  // Recipients per intent in compose-step order, which the "Share as" segment
  // reads to decide whether it has a choice to offer and how to word it.
  recipientCountsByIntent: readonly number[];
  // Recipients per group-text leg; used to title compose steps.
  maxRecipientsPerLeg: number;
  /** Every field's answer, keyed by field id. */
  fieldValues: Record<string, string>;
  setFieldValue: (id: string, value: string) => void;
  /** The words last left in each select's write-in, keyed by field id. */
  writeIns: Record<string, string>;
  setWriteIn: (id: string, words: string) => void;

  templateSets: readonly TemplateSet[];
  selectedTemplateSet: TemplateSet | null;
  /**
   * The impression-goal prose (the form's `goal` slot text, trimmed), for
   * surfaces that show the sentence outside a composed message body.
   * '' when the form in effect carries no goal slot.
   */
  goalProse: string;
  getTemplateForIntent: (intent: MessageIntent) => Template | null;
  /**
   * The garden form one intent is written from, as authored, or null on a
   * topic that composes the classic way. What the composer's Form tab asks.
   */
  getFormForIntent: (
    intent: MessageIntent
  ) => readonly SharingFormSlot[] | null;
  /**
   * Every slot of the topic's garden form, across all its intents, carrying
   * the sender's answer and keyed by slot id. Empty on a classic topic.
   */
  slotsById: Readonly<Record<string, AnsweredSlot>>;
  /**
   * The field id of the slot in one intent's form the allowed detail comes
   * from, or null when it has none. Only an intro carries an allowed detail;
   * the composer notes that the slot may be left blank.
   */
  allowedDetailSlotIdForIntent: (intent: MessageIntent) => string | null;
  /**
   * How many people one intent's message reaches: the sender's "Share as"
   * choice, narrowed to `dm` for an intent with a single recipient.
   */
  addressingModeForIntent: (intent: MessageIntent) => AddressingMode;
  /**
   * The environment one garden intent's message composes in, for all its
   * recipients (see `Environment`).
   */
  getEnvironmentForIntent: (intent: MessageIntent) => Environment<FormFacts>;
  /**
   * The same for the message to one recipient, with their own details marked
   * so a control can show them as theirs.
   */
  getMarkedEnvironmentForRecipient: (
    intent: MessageIntent,
    recipient: Contact
  ) => Environment<FormFacts>;
  // GARDEN-CUTOVER(#658): a classic template reads its primitives from a map.
  getContextForIntent: (intent: MessageIntent) => ContextVars;
  // Context resolved against a single recipient — used to preview how a
  // singleton message personalizes for one specific person.
  getContextForContact: (contact: Contact) => ContextVars;
  // END GARDEN-CUTOVER(#658)
  getPreviewForIntent: (intent: MessageIntent) => string;
  /**
   * The message `recipient` will receive, cut into pieces at their own details
   * so the preview sheet can pick those out. For Tyler, "Hey Tyler, it's
   * Robin." comes back as three pieces:
   *
   *   { text: 'Hey ', perRecipient: false }
   *   { text: 'Tyler', perRecipient: true }
   *   { text: ", it's Robin.", perRecipient: false }
   *
   * Joined in order, the pieces are the whole message. The `perRecipient` ones
   * change from one recipient to the next, and the sheet renders them as the
   * chip that previews the message to someone else.
   */
  getPreviewSegmentsForRecipient: (
    intent: MessageIntent,
    recipient: Contact
  ) => readonly PreviewSegment[];
  /**
   * This message as someone with connections back to the sender would send
   * it, `degree` degrees removed (one unless given): the same form and the same field
   * values, composed for made-up people (a recipient, a sender, and the
   * resharers in between) instead of this sender's own recipients. The story
   * still ends with this sender's allowed name. What the preview sheets show.
   */
  getHypotheticalPreviewForIntent: (
    intent: MessageIntent,
    degree?: number
  ) => string;
  /** Pick new made-up people for the next look at the hypothetical preview. */
  cycleHypotheticalPeople: () => void;
  getContactsForIntent: (intent: MessageIntent) => Contact[];
  /**
   * The intents this session writes a message for — those with anyone to
   * message — in compose-step order.
   */
  intentsWithRecipients: readonly MessageIntent[];
  isTemplateHydrated: (intent: MessageIntent) => boolean;
  /**
   * Whether a slot is soft-linked to one asked in an earlier message of this
   * share, and so left off the form (see `ComposeLogic.linkedAnswerSources`).
   */
  isAnsweredByLinkedSlot: (slotId: string) => boolean;
  /**
   * Store the sender's answer to a slot, copied to the slots linked to it
   * that they are not asked on their own (see `ComposeLogic.answerLinked`).
   */
  answerSlot: (slotId: string, answer: string) => void;

  recipients: ContactList;
  confirmedContactIds: ReadonlySet<string>;
  actionAwarenessMap: TopicAwarenessMap;
  /**
   * Whether the awareness check has answered for the recipients as they are
   * now. Each recipient's intent comes from awareness, so the recipients step
   * cannot advance before this.
   */
  recipientsAwarenessChecked: boolean;
  /**
   * "Exclude recipients who've seen this topic before" (#581). While on, a
   * contact whose awareness says `introducedByAnyone` is blocked outright instead
   * of being routed to the reminder (or reshare) they'd otherwise get.
   */
  excludeAwareContacts: boolean;
  /**
   * Records the sender's own choice, which then outranks the news-topic default
   * for the rest of the session and is stored either way.
   */
  setExcludeAwareContacts: (value: boolean) => void;
  /**
   * Ask the server which of these contacts have already seen the topic, merging
   * the answer into the session awareness cache. Fire-and-forget, and each
   * phone is asked about only once per topic — awareness costs OPRF hashing
   * plus a round trip, so RecipientsStep calls this a screenful at a time
   * rather than for the whole address book (#581).
   */
  requestAwarenessFor: (contacts: readonly Contact[]) => void;
  contactIntents: Map<string, MessageIntent>;
  introBlockedKeys: Set<string>;
  contactsToSend: Contact[];
  /**
   * Every message this share sends as the "Share as" choice
   * cuts them: one per recipient as direct texts, or group texts of up to
   * `MAX_GRP_TXT_RECIPIENTS` people each.
   */
  sendPlan: MessageDraft[];

  /**
   * Where the sender is in the flow and how they move through it. The session
   * state above holds what they have chosen and knows nothing about steps.
   */
  nav: ShareNavigator;

  /**
   * Progress of the update-flow recipient match (labeled OPRF-PSI over the
   * address book): `{ done, total }` while hashing, `null` when idle/finished.
   * Drives the RecipientsStep progress indicator (#427).
   */
  recipientMatchProgress: { done: number; total: number } | null;

  isRecipientBoxExpanded: boolean;
  setIsRecipientBoxExpanded: (b: boolean) => void;

  isLoadingLegState: boolean;
  inProgressLegId: string | null;
  sendQueueCursor: number;
  sendQueue: MessageDraft[];

  showAddContactModal: boolean;
  addContactInitialName: string;

  interstitialsShown: ReadonlySet<string>;
  recordInterstitialShown: (key: string) => void;

  handleComposeNext: (intent: MessageIntent) => Promise<void>;
  /**
   * The garden composer's "Queue Msgs for Sharing": start this flow's sharing
   * run, put the send plan in the outbox with every leg's details resolved,
   * leave the flow and open the outbox on it. The outbox sends from there on its own.
   */
  handleQueueToOutbox: () => Promise<void>;

  handleAddRecipients: (contacts: Contact[]) => void;
  handleAddRecipientsProvisionally: (contacts: Contact[]) => void;
  handleRemoveRecipient: (index: number) => void;

  impliedConfirm: boolean;
  setImpliedConfirm: (value: boolean) => void;

  totalConfirmedCount: number;
  handleOpenFirstShare: () => Promise<void>;
  handleConfirmAndOpenNext: () => Promise<void>;
  handleConfirmFinalShare: () => Promise<void>;
  handleConfirmShare: () => Promise<void>;
  handleRetryCurrentShare: () => void;
  handleCancelSending: () => void;
  handleInterruptSending: () => void;

  showInterruptWarning: boolean;
  setShowInterruptWarning: (b: boolean) => void;
  showCloseWarning: boolean;
  setShowCloseWarning: (b: boolean) => void;

  handleRequestAddContact: (
    initialName: string,
    onSave: (contact: Contact) => void
  ) => void;
  handleCloseAddContactModal: () => void;
  handleSaveAddedContact: (contact: Contact) => void;

  // Signals that the wizard page's enter transition has finished, gating the
  // deferred pre-send recipient restore (#479) so it never dispatches into
  // ContactStateContext mid-transition (which wedges the page blank). Called by
  // ShareWizardContent from viewDidEnter.
  notifyViewSettled: () => void;
}

const ShareWizardContext = createContext<ShareWizardContextValue | undefined>(
  undefined
);

interface ShareWizardProviderProps {
  topicId: string;
  children: ReactNode;
}

export const ShareWizardProvider: React.FC<ShareWizardProviderProps> = ({
  topicId,
  children,
}) => {
  const [presentToast] = useAppToast();
  const { state, dispatch, isLoading: contactsLoading } = useContactState();
  const {
    familiarName: userName,
    allowedName,
    preferredPronouns,
  } = useUserContext();
  const { permissionsMap, can } = useCasbin();
  const deviceInfo = useDeviceInfo();
  const fcmToken = useFCMToken();

  // aboutChangesSince — the head of the change list being shared — both signals
  // the update flow and drives the Changes step ("Since {timestamp}" + recipient
  // filtering). Its value comes entirely from persisted wizard state, restored
  // below: bootstrapped (with the summary) when the user taps Share Updates /
  // Reshare, and also present for a sender returning from SMS. Persisted state is
  // the single carrier — no URL param. (#427)

  // Reshare mode (#447): the leg being passed on. Holding one IS the reshare
  // signal, the same way a persisted aboutChangesSince signals the update flow
  // — there is no second flag that could disagree with it. Bootstrapped by the
  // news welcome sheet's reshare CTA, restored below.

  // The starting leg of the sharing run this flow started (#455), created just before
  // the flow enters its share-confirm steps. Every topic type gets one, so a
  // "sharing run" — the indefinite propagation of a topic — is trackable for
  // any topic, not just news. The ref mirrors the state for synchronous reads
  // inside openSMSLinkForLeg, which may create the starting leg and reference it in
  // one tick.

  const { features } = useAppConfig();
  const { queueSend, openOutbox } = useOutbox();
  const [topic, setTopic] = useState<Topic | null>(null);
  const [isTopicLoading, setIsTopicLoading] = useState<boolean>(true);
  const [topicError, setTopicError] = useState<string | null>(null);
  const {
    selectedToneId,
    setSelectedToneId,
    singletonMode,
    setSingletonMode,
    fieldValues,
    setFieldValues,
    setFieldValue,
    writeIns,
    setWriteIn,
  } = useShareFormChoices(topicId);
  const [isRecipientBoxExpanded, setIsRecipientBoxExpanded] =
    useState<boolean>(true);

  const [showAddContactModal, setShowAddContactModal] =
    useState<boolean>(false);
  const [addContactInitialName, setAddContactInitialName] =
    useState<string>('');
  const [interstitialsShown, setInterstitialsShown] = useState<Set<string>>(
    new Set()
  );
  const [showInterruptWarning, setShowInterruptWarning] =
    useState<boolean>(false);
  const [showCloseWarning, setShowCloseWarning] = useState<boolean>(false);

  // Set once ShareWizardContent reports the enter transition finished
  // (viewDidEnter), which gates the deferred pre-send recipient restore.
  const [viewSettled, setViewSettled] = useState<boolean>(false);
  const notifyViewSettled = useCallback(() => setViewSettled(true), []);

  const recordInterstitialShown = useCallback((key: string) => {
    setInterstitialsShown(prev => new Set(Array.from(prev).concat(key)));
  }, []);

  const pendingAddContactSave = useRef<((contact: Contact) => void) | null>(
    null
  );

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setIsTopicLoading(true);
    setTopicError(null);

    (async () => {
      try {
        // TODO(https://github.com/kick-turn-solutions/snowball/issues/220)
        // Avoid heavyweight topic lookup or leverage caching
        const response = await apiCall(
          `/api/topics/${encodeURIComponent(topicId)}`,
          { signal: controller.signal }
        );
        if (cancelled) return;
        if (response.ok) {
          const data = await response.json();
          setTopic(TopicLogic.fromJSON(data));
        } else if (response.status === 404) {
          setTopicError('Topic not found');
        } else {
          setTopicError('Failed to load topic');
        }
      } catch {
        if (!cancelled && !controller.signal.aborted) {
          setTopicError('Network error');
        }
      } finally {
        if (!cancelled) setIsTopicLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [topicId]);

  const recipients = useMemo(() => selectRecipients(state), [state]);

  // A restored in-progress leg means the sender is mid-send and has already
  // been past the interstitials, so the wizard does not interrupt to re-ask.
  const markAllInterstitialsShown = useCallback(
    () => setInterstitialsShown(new Set(ALL_INTERSTITIAL_KEYS)),
    []
  );

  // Everything a reload must not lose: the flow this session is in, its send
  // queue, and the recipient selection — read from storage on mount and written
  // back as it changes. See useWizardSession.
  const session = useWizardSession({
    topicId,
    topic,
    isTopicLoading,
    recipients,
    contactsLoading,
    viewSettled,
    dispatch,
    presentToast,
    markAllInterstitialsShown,
  });
  const {
    aboutChangesSince,
    previousLegId,
    connectionToPrevLeg,
    inProgressLegId,
    sendQueue,
    sendQueueCursor,
    confirmedContactIds,
    excludeAwareContacts,
    updateSummary,
    sharingMode,
    isUpdateFlow,
    isLoadingLegState,
    setInProgressLegId,
    setSendQueue,
    setSendQueueCursor,
    setConfirmedContactIds,
    setUpdateSummary,
    setConnectionToPrevLeg,
    setExcludeAwareContacts,
    setSharingRunId,
    readSharingRunId,
    resetSession,
    clearStoredSession,
    persistNow,
  } = session;

  // Reshare mode only (#447): the previous leg's info payload — the source
  // of the reshare story block, race numbers, and the quoter's pronouns. Fetched from
  // the auth-optional /reshare-story endpoint (NOT the ActionRead-gated leg DTO),
  // so an unauthenticated reshare recipient can still load it. The endpoint
  // answers for every topic type (#602), so the compose step's wait on this
  // payload resolves whatever the topic is.
  const [reshareInfoForPrevLeg, setReshareInfoForPrevLeg] =
    useState<ReshareInfoLike | null>(null);
  useEffect(() => {
    if (!previousLegId) return;
    let cancelled = false;
    MePrFoService.getReshareInfoForLeg(previousLegId)
      .then(payload => {
        if (cancelled) return;
        setReshareInfoForPrevLeg(payload);
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error('Failed to load parent reshare info payload:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [previousLegId]);
  // The last initiation response's details payload (#447): its
  // runImpressionCount already includes this session's sends, so the
  // last-confirm step derives the goal-crossing celebration from it. Session
  // state only — a cold restart mid-handoff just skips the celebration.

  // Live snapshots of the address book and recipient count for the one-shot
  // prepopulation effect below. It reads these fresh at run time instead of
  // depending on them, so an unrelated address-book mutation mid-run (e.g. the
  // awareness effect enriching a contact) can't tear the effect down and abort
  // the in-flight OPRF hashing, which would leave the recipient box blank (#436).

  // Ask the real scoped share permission rather than using ACTIONS.UPDATE as a
  // manager proxy: for an only-managers-can-share topic the backend denies
  // non-managers, while everyone can share an unrestricted topic.
  const [canShare, setCanShare] = useState(false);
  useEffect(() => {
    can(ACTIONS.SHARE, RESOURCES.TOPICS, topicId).then(setCanShare);
  }, [can, topicId, permissionsMap]);

  const introLimitReached = useMemo(() => {
    if (!topic) return false;
    // The effective ceiling is the smaller of the topic's own limit and the global
    // introCountMax; either may be unset.
    const eff = effectiveIntroLimit(
      topic.introCountLimit,
      features.introCountMax
    );
    if (eff != null && (topic.introCount ?? 0) >= eff) {
      return true;
    }
    // No share permission on this topic (e.g. only-managers-can-share and the
    // user is not a manager) blocks adding new invitees.
    if (!canShare) {
      return true;
    }
    return false;
  }, [topic, canShare, features.introCountMax]);

  const {
    awarenessMap: actionAwarenessMap,
    recipientsAwarenessChecked,
    requestAwarenessFor,
    enrichResolvedIds,
    recordSentLeg,
  } = useTopicAwareness({
    topicId,
    topic,
    recipients,
    dispatch,
    setConfirmedContactIds,
  });

  const collapseRecipientBox = useCallback(
    () => setIsRecipientBoxExpanded(false),
    []
  );

  const { recipientMatchProgress } = useUpdateRecipientMatch({
    isUpdateFlow,
    topic,
    isLoadingLegState,
    inProgressLegId,
    aboutChangesSince,
    state,
    recipients,
    dispatch,
    enrichResolvedIds,
    collapseRecipientBox,
  });

  const {
    contactsByIntent,
    introBlockedKeys,
    contactIntents,
    recipientCountsByIntent,
    effectiveSingletonMode,
    intentReachesOnePerson,
    getContactsForIntent,
  } = useShareRecipients({
    recipients,
    confirmedContactIds,
    awarenessMap: actionAwarenessMap,
    isUpdateFlow,
    introLimitReached,
    excludeAwareContacts,
    singletonMode,
    dispatch,
    presentToast,
  });

  const {
    templateSets,
    selectedTemplateSet,
    goalProse,
    getTemplateForIntent,
    getFormForIntent,
    slotsById,
    addressingModeForIntent,
    allowedDetailForIntent,
    allowedDetailSlotIdForIntent,
    getEnvironmentForIntent,
    getMarkedEnvironmentForRecipient,
    getContextForIntent,
    getContextForContact,
    getPreviewForIntent,
    getPreviewSegmentsForRecipient,
    getHypotheticalPreviewForIntent,
    cycleHypotheticalPeople,
    isTemplateHydrated,
    isAnsweredByLinkedSlot,
    answerSlot,
    composeForIntent,
  } = useShareComposer({
    topic,
    features,
    sharingMode,
    isUpdateFlow,
    updateSummary,
    previousLegId: previousLegId ?? null,
    reshareInfoForPrevLeg,
    userName,
    allowedName,
    connectionToPrevLeg,
    preferredPronouns,
    contactsByIntent,
    intentReachesOnePerson,
    selectedToneId,
    setFieldValues,
    fieldValues,
    presentToast,
  });

  const sendPlan = useMemo(
    () =>
      topic
        ? ShareWizardLogic.buildSendPlan({
            topic,
            contactsByIntent,
            sharingMode,
            maxRecipientsPerLeg: effectiveSingletonMode
              ? 1
              : MAX_GRP_TXT_RECIPIENTS,
            composeBody: composeForIntent,
          })
        : [],
    [
      topic,
      composeForIntent,
      contactsByIntent,
      effectiveSingletonMode,
      sharingMode,
    ]
  );

  const intentsWithRecipients = useMemo(
    () => ShareWizardLogic.intentsWithRecipients(contactsByIntent),
    [contactsByIntent]
  );

  const contactsToSend = useMemo(
    () => sendPlan.flatMap(draft => draft.header.recipients),
    [sendPlan]
  );

  // What the navigator needs to know about this session — which destinations
  // the flow shows and which queued leg the sender is on. Nothing here is a
  // step or an index: turning this into a sequence is the navigator's job.
  const flow = useMemo<ShareFlowShape>(
    () => ({
      contactsByIntent,
      sharingMode,
      isUpdateFlow,
      sendQueueLength: sendQueue.length,
      sendQueueCursor,
    }),
    [
      contactsByIntent,
      isUpdateFlow,
      sendQueue.length,
      sendQueueCursor,
      sharingMode,
    ]
  );

  // Which surface is rendering this session. The garden flow and the wizard
  // read the same session state and ask for the same destinations; the surface is
  // what turns a destination into the URL of the screen that shows it.
  const surface: ShareSurface = topicGardenEnabled(features)
    ? 'garden'
    : 'wizard';

  const nav = useShareNavigator({
    topicId,
    surface,
    flow,
    discardSession: clearStoredSession,
  });

  // ── what the send machine asks the composer ───────────────────────────────
  // useShareController owns sending and knows nothing about templates or field
  // values; these three are the whole of what it asks for. They are also the
  // seam the sending steps will move behind when they get a route of their own:
  // each has to become answerable from the stored session rather than from live
  // compose state, and nothing else in the send machine reaches back here.

  /**
   * The draft's body as it will be texted. The draft already carries a composed
   * body, but the leg link can only be woven in once the leg exists — which is
   * halfway through the handoff, hence the second pass.
   */
  const finalizeBody = useCallback(
    (draft: MessageDraft, legId: string): string =>
      composeForIntent(draft.header.intent, draft.header.recipients, legId) ||
      draft.body,
    [composeForIntent]
  );

  /**
   * The sender's own sharing-story contributions for one leg. The allowed
   * detail comes from the sender's answers to this message's form — only the
   * slots this sharing mode asks, so an answer left over from introducing the
   * topic never rides along on a reshare (see `allowedDetailForIntent`). The
   * pronouns are profile data rather than a wizard field (#506).
   */
  const legStoryFor = useCallback(
    (draft: MessageDraft): LegStoryInfo => {
      const form = getFormForIntent(draft.header.intent);
      return {
        allowedDetail: allowedDetailForIntent(draft.header.intent),
        senderPronouns: preferredPronouns,
        // The name the sender agrees to travel under in the case of a reshare.
        // The draft says whether this is one rather than the intent, because a
        // reshare carries whichever intent its recipient needs.
        allowedSenderName:
          draft.header.type === 'reshare' ? allowedName : undefined,
        // Only the garden composer asks for the connection. A classic topic
        // leaves it unset, and the server's story reads "I know".
        connectionToPrevLeg: form ? connectionToPrevLeg.trim() : undefined,
      };
    },
    [
      allowedName,
      allowedDetailForIntent,
      connectionToPrevLeg,
      getFormForIntent,
      preferredPronouns,
    ]
  );

  /**
   * What the starting leg of this flow's sharing run records about the sender
   * (#455). A run's story is frozen when the flow commits to sending, and every
   * leg below the start tells its story through it, so this is read once and
   * then fixed for the whole run.
   *
   * The allowed detail comes from the sender's answers to the intent being
   * composed, as in `legStoryFor`; the pronouns are profile data rather than a
   * wizard field (#506);
   * the allowed name is whatever name the sender has agreed to let travel.
   * Blank contributions are dropped by the service rather than here, so this
   * can hand over whatever the sender has without checking.
   */
  const startingLegStoryFor = useCallback(
    (intent: MessageIntent): LegStoryInfo => ({
      allowedDetail: allowedDetailForIntent(intent),
      senderPronouns: preferredPronouns,
      allowedSenderName: allowedName,
    }),
    [allowedName, allowedDetailForIntent, preferredPronouns]
  );

  const {
    ensureSharingRun,
    handleOpenFirstShare,
    handleRetryCurrentShare,
    handleConfirmShare,
    handleConfirmAndOpenNext,
    handleConfirmFinalShare,
    impliedConfirm,
    setImpliedConfirm,
    totalConfirmedCount,
    lastReshareInfo,
    handleCancelSending,
    handleInterruptSending,
  } = useShareController({
    topic,
    finalizeBody,
    legStoryFor,
    startingLegStoryFor,
    sendQueue,
    sendQueueCursor,
    inProgressLegId,
    previousLegId,
    aboutChangesSince,
    updateSummary,
    setInProgressLegId,
    setSendQueue,
    setSendQueueCursor,
    setConfirmedContactIds,
    setSharingRunId,
    readSharingRunId,
    resetSession,
    clearStoredSession,
    persistNow,
    nav,
    recipients,
    dispatch,
    deviceInfo,
    fcmToken,
    presentToast,
    onLegSent: recordSentLeg,
  });

  /**
   * Commit to the send plan and enter the wizard's send loop. `intent` is
   * the message step the sender commits from.
   */
  const startSharing = useCallback(
    async (intent: MessageIntent) => {
      if (sendPlan.length === 0) {
        presentToast({
          message: 'No one left to message for this step',
          duration: 3000,
          color: 'warning',
        });
        return;
      }
      // Mark the start of a sharing run unless it's a reshare, which can just
      // add legs to a run that's already been started.
      if (sharingMode !== 'reshare') {
        try {
          await ensureSharingRun(intent);
        } catch (error) {
          presentToast({
            message: "Couldn't start sharing. Please try again.",
            duration: 3000,
            color: 'danger',
          });
          // eslint-disable-next-line no-console
          console.error('Failed to start sharing run:', error);
          return;
        }
      }
      setSendQueue(sendPlan);
      setSendQueueCursor(0);
      nav.goTo(Destinations.send, 'forward');
    },
    [
      sendPlan,
      ensureSharingRun,
      nav,
      presentToast,
      setSendQueue,
      setSendQueueCursor,
      sharingMode,
    ]
  );

  const queueToOutbox = useCallback(async () => {
    if (!topic || sendPlan.length === 0) {
      presentToast({
        message: 'No one left to message for this step',
        duration: 3000,
        color: 'warning',
      });
      return;
    }
    // A reshare adds its legs to the run it received; anything else starts
    // a run of its own for them to hang off (#455).
    let runStartLegId: string | undefined;
    if (sharingMode !== 'reshare') {
      try {
        runStartLegId = await ensureSharingRun(sendPlan[0].header.intent);
      } catch (error) {
        presentToast({
          message: "Couldn't start sharing. Please try again.",
          duration: 3000,
          color: 'danger',
        });
        // eslint-disable-next-line no-console
        console.error('Failed to start sharing run:', error);
        return;
      }
    }
    const items = sendPlan.map(draft =>
      OutboxLogic.itemFor(
        draft,
        finalizeBody(draft, OUTBOX_LEG_ID_PLACEHOLDER),
        sharingLegDetailsFor(draft, {
          topicId: topic.id,
          story: legStoryFor(draft),
          previousLegId:
            draft.header.type === 'reshare' ? previousLegId : runStartLegId,
          updateSummary,
          aboutChangesSince,
        })
      )
    );
    const queuedAt = new Date().toISOString();
    await queueSend(
      OutboxLogic.newSend({
        id: `${topic.id}@${queuedAt}`,
        topicId: topic.id,
        topicTitle: topic.title,
        queuedAt,
        items,
      })
    );
    nav.exitAndDiscard();
    openOutbox(topic.id);
  }, [
    aboutChangesSince,
    ensureSharingRun,
    finalizeBody,
    legStoryFor,
    nav,
    openOutbox,
    presentToast,
    previousLegId,
    queueSend,
    sendPlan,
    sharingMode,
    topic,
    updateSummary,
  ]);

  // A second call while the share is still being queued joins the first
  // rather than starting another run and replacing the send it queues.
  const queuingRef = useRef<Promise<void> | null>(null);
  const handleQueueToOutbox = useCallback(() => {
    if (queuingRef.current) return queuingRef.current;
    const queuing = queueToOutbox().finally(() => {
      queuingRef.current = null;
    });
    queuingRef.current = queuing;
    return queuing;
  }, [queueToOutbox]);

  const handleComposeNext = useCallback(
    async (intent: MessageIntent) => {
      if (!isTemplateHydrated(intent)) {
        presentToast({
          message: 'Please finish the message',
          duration: 3000,
          color: 'warning',
        });
        return;
      }
      if (intent === 'intro' || intent === 'reminder' || intent === 'update') {
        const nextCompose = nav.nextCompose(intent);
        if (nextCompose) {
          nav.goTo(nextCompose, 'forward');
          return;
        }
      }
      await startSharing(intent);
    },
    [isTemplateHydrated, nav, presentToast, startSharing]
  );

  const handleAddRecipients = useCallback(
    (contacts: Contact[]) => {
      dispatch({ type: 'ADD_RECIPIENTS', payload: contacts });
    },
    [dispatch]
  );

  const handleAddRecipientsProvisionally = useCallback(
    (contacts: Contact[]) => {
      dispatch({ type: 'PROVISIONALLY_ADD_RECIPIENTS', payload: contacts });
    },
    [dispatch]
  );

  const handleRemoveRecipient = useCallback(
    (index: number) => {
      const recipientId = state.recipientIds[index];
      if (recipientId) {
        dispatch({
          type: 'REMOVE_RECIPIENT',
          payload: { localId: recipientId },
        });
      }
    },
    [dispatch, state.recipientIds]
  );

  const handleRequestAddContact = useCallback(
    (initialName: string, onSave: (contact: Contact) => void) => {
      setAddContactInitialName(initialName);
      pendingAddContactSave.current = onSave;
      setShowAddContactModal(true);
    },
    []
  );

  const handleCloseAddContactModal = useCallback(() => {
    setShowAddContactModal(false);
    setAddContactInitialName('');
    pendingAddContactSave.current = null;
  }, []);

  const handleSaveAddedContact = useCallback(
    (contact: Contact) => {
      pendingAddContactSave.current?.(contact);
      pendingAddContactSave.current = null;
      handleCloseAddContactModal();
    },
    [handleCloseAddContactModal]
  );

  useEffect(() => {
    return () => {
      dispatch({ type: 'CLEAR_RECIPIENTS' });
    };
  }, [dispatch]);

  const value: ShareWizardContextValue = {
    isUpdateFlow,
    topic,
    isTopicLoading,
    topicError,
    topicId,
    aboutChangesSince,
    updateSummary,
    setUpdateSummary,
    sharingMode,
    connectionToPrevLeg,
    setConnectionToPrevLeg,
    reshareInfoForPrevLeg,
    lastReshareInfo,

    selectedToneId,
    setSelectedToneId,
    singletonMode,
    setSingletonMode,
    effectiveSingletonMode,
    intentReachesOnePerson,
    recipientCountsByIntent,
    maxRecipientsPerLeg: MAX_GRP_TXT_RECIPIENTS,
    fieldValues,
    setFieldValue,
    writeIns,
    setWriteIn,

    templateSets,
    selectedTemplateSet,
    goalProse,
    getTemplateForIntent,
    getFormForIntent,
    slotsById,
    allowedDetailSlotIdForIntent,
    addressingModeForIntent,
    getEnvironmentForIntent,
    getMarkedEnvironmentForRecipient,
    getContextForIntent,
    getContextForContact,
    getPreviewForIntent,
    getPreviewSegmentsForRecipient,
    getHypotheticalPreviewForIntent,
    cycleHypotheticalPeople,
    getContactsForIntent,
    intentsWithRecipients,
    isTemplateHydrated,
    isAnsweredByLinkedSlot,
    answerSlot,

    recipients,
    confirmedContactIds,
    actionAwarenessMap,
    recipientsAwarenessChecked,
    excludeAwareContacts,
    setExcludeAwareContacts,
    requestAwarenessFor,
    contactIntents,
    introBlockedKeys,
    contactsToSend,
    sendPlan,

    nav,
    recipientMatchProgress,

    isRecipientBoxExpanded,
    setIsRecipientBoxExpanded,

    isLoadingLegState,
    inProgressLegId,
    sendQueueCursor,
    sendQueue,

    showAddContactModal,
    addContactInitialName,

    interstitialsShown,
    recordInterstitialShown,

    handleComposeNext,
    handleQueueToOutbox,

    handleAddRecipients,
    handleAddRecipientsProvisionally,
    handleRemoveRecipient,

    impliedConfirm,
    setImpliedConfirm,

    totalConfirmedCount,
    handleOpenFirstShare,
    handleConfirmAndOpenNext,
    handleConfirmFinalShare,
    handleConfirmShare,
    handleRetryCurrentShare,
    handleCancelSending,
    handleInterruptSending,

    showInterruptWarning,
    setShowInterruptWarning,
    showCloseWarning,
    setShowCloseWarning,

    handleRequestAddContact,
    handleCloseAddContactModal,
    handleSaveAddedContact,

    notifyViewSettled,
  };

  return (
    <ShareWizardContext.Provider value={value}>
      {children}
    </ShareWizardContext.Provider>
  );
};

export const useShareWizardContext = (): ShareWizardContextValue => {
  const ctx = useContext(ShareWizardContext);
  if (!ctx) {
    throw new Error(
      'useShareWizardContext must be used within a ShareWizardProvider'
    );
  }
  return ctx;
};
